import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Image, Palette, CalendarDays, ShoppingBag, Plus, Clock } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: number; color: string; delay: number }> = ({ icon, label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6"
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${color}`}>
      {icon}
    </div>
    <p className="text-3xl font-syncopate font-bold tracking-tight">{value}</p>
    <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">{label}</p>
  </motion.div>
);

const AdminDashboard: React.FC = () => {
  const { localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const artworks = useAdminStore((s) => s.artworks);
  const artists = useAdminStore((s) => s.artists);
  const exhibitions = useAdminStore((s) => s.exhibitions);
  const orders = useAdminStore((s) => s.orders);
  const activityLog = useAdminStore((s) => s.activityLog);

  const currentExhibitions = (exhibitions ?? []).filter((e) => e.isCurrent).length;
  const pendingOrders = (orders ?? []).filter((o) => o.status === 'pending').length;
  const recentActivity = [...(activityLog ?? [])].reverse().slice(0, 8);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Dashboard</h1>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{localize(settings?.galleryName) || 'Gallery Fotoatelier'}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Image size={18} className="text-black/60 dark:text-white/60" />} label="Artworks" value={(artworks ?? []).length} color="bg-black/[0.04] dark:bg-white/[0.06]" delay={0} />
        <StatCard icon={<Palette size={18} className="text-black/60 dark:text-white/60" />} label="Artists" value={(artists ?? []).length} color="bg-black/[0.04] dark:bg-white/[0.06]" delay={0.05} />
        <StatCard icon={<CalendarDays size={18} className="text-black/60 dark:text-white/60" />} label="Active Exhibitions" value={currentExhibitions} color="bg-black/[0.04] dark:bg-white/[0.06]" delay={0.1} />
        <StatCard icon={<ShoppingBag size={18} className="text-black/60 dark:text-white/60" />} label="Pending Orders" value={pendingOrders} color="bg-black/[0.04] dark:bg-white/[0.06]" delay={0.15} />
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-3">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/admin/artworks/new', label: 'Add Artwork' },
            { to: '/admin/artists/new', label: 'Add Artist' },
            { to: '/admin/exhibitions/new', label: 'New Exhibition' },
            { to: '/admin/art-fairs/new', label: 'New Art Fair' },
          ].map((action) => (
            <Link
              key={action.to}
              to={action.to}
              className="flex items-center gap-2 px-4 py-2.5 border border-black/[0.06] dark:border-white/[0.06] rounded-xl text-[10px] font-bold uppercase tracking-[0.1em] text-black/40 dark:text-white/40 hover:border-black/15 dark:hover:border-white/15 hover:text-black dark:hover:text-white transition-all"
            >
              <Plus size={12} />
              {action.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-3">Recent Activity</h3>
        {recentActivity.length === 0 ? (
          <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-8 text-center">
            <Clock size={24} className="mx-auto text-black/10 dark:text-white/10 mb-3" />
            <p className="text-[11px] text-black/30 dark:text-white/30 font-medium">No recent activity</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl divide-y divide-black/[0.03] dark:divide-white/[0.03]">
            {recentActivity.map((entry) => (
              <div key={entry._id} className="flex items-center gap-3 px-5 py-3.5">
                <div className={`w-2 h-2 rounded-full shrink-0 ${
                  entry.action === 'created' ? 'bg-green-500' :
                  entry.action === 'updated' ? 'bg-blue-500' :
                  entry.action === 'deleted' ? 'bg-red-500' : 'bg-gray-400'
                }`} />
                <span className="text-[11px] font-medium text-black/60 dark:text-white/60 capitalize">{entry.action}</span>
                <span className="text-[11px] text-black/30 dark:text-white/30">{entry.entityType}</span>
                <span className="text-[11px] font-bold text-black/70 dark:text-white/70 truncate">{entry.entityLabel}</span>
                <span className="ml-auto text-[9px] text-black/20 dark:text-white/20 shrink-0">
                  {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
